import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import * as FeedbacksActions from 'Shared/actions/Feedbacks.actions';

// Components
import { WpContainer, WpContainerBody, WpContainerHeader } from 'Wpds/components/container';
import LoadingShimmer from 'Shared/components/LoadingShimmer/LoadingShimmer';
import { WpListSelect } from 'Wpds/components/list';
import WpAlert from 'Wpds/components/alert/WpAlert';
import WpButton from 'Wpds/components/button/WpButton';

// Styles
import './GPT3StepChoose.scss';

type Props = {
  strengths: Array<any>;
  improvements: Array<any>;
  strengthExamples: string;
  improvementExamples: string;
  selectedOption: string;
  generatedOptions: Array<any>;
  setGeneratedOptions: Function;
  setSelectedOption: Function;
  shouldFetch?: boolean;
};

const GPT3StepChoose = ({
  setSelectedOption,
  strengths,
  improvements,
  strengthExamples,
  improvementExamples,
  selectedOption,
  generatedOptions,
  setGeneratedOptions,
  shouldFetch = true,
}: Props) => {
  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const fetchSuggestions = () => {
    toggleIsLoading();
    const strengthsList = strengths.map(strength => strength.value);
    const improvementsList = improvements.map(improvement => improvement.value);
    dispatch(
      FeedbacksActions.fetchAIFeedbackSuggestions({
        strengths: strengthsList,
        strengthExamples: strengthExamples,
        weaknesses: improvementsList,
        weaknessExamples: improvementExamples,
      })
    )
      .then(response => {
        setGeneratedOptions(response.data);
        setErrorMessage(null);
        setSelectedOption('');
      })
      .catch(error => {
        setErrorMessage(error?.response?.data?.error || 'Unknown error. Please try again.');
      })
      .finally(() => {
        toggleIsLoading();
      });
  };

  useEffect(() => {
    if (shouldFetch) {
      fetchSuggestions();
    }
  }, [strengths, improvements, shouldFetch]);

  const handleSelection = selected => {
    if (selected.length > 0) setSelectedOption(selected[0].value);
    else setSelectedOption('');
  };

  const baseClassName = 'GPT3StepChoose';

  if (errorMessage && generatedOptions.length === 0) {
    return <WpAlert type="danger">{errorMessage}</WpAlert>;
  }

  return (
    <WpContainer className={baseClassName}>
      {!isLoading && generatedOptions.length > 0 && (
        <WpContainerHeader>
          <div className={`${baseClassName}__title`}>Choose a suggestion to insert and edit in the feedback form</div>
        </WpContainerHeader>
      )}
      <WpContainerBody>
        {isLoading ? (
          <>
            <p>
              <em>Sit tight, FeedbackAI is loading... this can take up to 30 seconds</em>
            </p>
            <div className="mb-2">
              <LoadingShimmer shape="rectangle" height="16px" width="60%" />
            </div>
            <div className="mb-2">
              <LoadingShimmer shape="rectangle" height="16px" width="60%" />
            </div>
            <div className="mb-2">
              <LoadingShimmer shape="rectangle" height="16px" width="60%" />
            </div>
          </>
        ) : (
          <>
            <WpListSelect
              id="gpt-generated-options"
              options={generatedOptions}
              max={1}
              iconNotSelected="circle"
              onChange={handleSelection}
              selectedOptions={selectedOption !== '' && [selectedOption]}
              qaTest="choose-suggestion-select-list"
            />
            <div className="mt-1">
              <WpButton size="md" variant="secondary" onClick={fetchSuggestions}>
                Generate new suggestions
              </WpButton>
            </div>
            {errorMessage && (
              <div className="mt-2">
                <WpAlert type="danger">{errorMessage}</WpAlert>
              </div>
            )}
          </>
        )}
      </WpContainerBody>
    </WpContainer>
  );
};

export default GPT3StepChoose;
